import { MODULE_TYPE, Pharmacy } from '@abiglobalhealth/go-types'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPharmacies, setFulfillmentCollect } from '@/api/pharmacy'
import { BackgroundPattern } from '@/components/BackgroundPattern'
import { Button } from '@/components/Button'
import { Search } from '@/components/icons/Search'
import { useButtonClick } from '@/hooks/useButtonClick'
import { useConsultation } from '@/hooks/useConsultation'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { usePharmacy } from '@/hooks/usePharmacy'
import { useUser } from '@/hooks/useUser'

import { PharmacyCard } from './PharmacyCard'

export const HealthMailPharmacySelector = () => {
  const { currentModule } = useModule()

  const { user } = useUser()
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>(() => {
    if (currentModule?.type !== MODULE_TYPE.PHARMACY_SELECTOR) return []
    return currentModule.pharmacies
  })

  const { t } = useTranslation()
  const { handleButtonClick } = useButtonClick()
  const [searchText, setSearchText] = useState('')
  const { selectedPharmacy, updateSelectedPharmacy } = usePharmacy()
  const [error, setError] = useState<string | null>(null)
  const { loading, showLoading, hideLoading } = useLoading()
  const { updateConsultation } = useConsultation()

  const filteredPharmacies = useMemo(() => {
    return pharmacies?.filter((pharmacy) => `${pharmacy.name} ${pharmacy.address}`.toLowerCase().includes(searchText.toLowerCase()))
  }, [pharmacies, searchText])

  const handlePharmacySelected = (pharmacy: Pharmacy) => {
    setError(null)
    updateSelectedPharmacy(pharmacy)
  }

  const handleContinue = async () => {
    if (!selectedPharmacy) {
      setError('pharmacy_selector.validation')
      return
    }

    if (currentModule?.type === MODULE_TYPE.PHARMACY_SELECTOR) {
      handleButtonClick(currentModule.buttonAction)
      return
    }

    showLoading()

    const consultation = await setFulfillmentCollect({
      uniqueId: user?.data?.uniqueId!,
      pharmacyId: String(selectedPharmacy.id)
    })
    updateConsultation(consultation)

    hideLoading()
  }

  useEffect(() => {
    if (!currentModule || currentModule.type === MODULE_TYPE.PHARMACY_SELECTOR) return

    getPharmacies({
      filter: '',
      uniqueId: user?.data?.uniqueId!
    }).then((res) => {
      setPharmacies(res as Pharmacy[])
    })
  }, [])

  return (
    <BackgroundPattern className="bg-gray-background flex grow flex-col gap-4 overflow-auto p-4">
      <h2>{t('pharmacy_selector.description')}</h2>

      <span className="relative">
        <Search customClassName="text-gray-text absolute top-1/2 transform -translate-y-1/2 right-4 focus-within:text-primary" />
        <input
          className="border-gray focus-visible:border-primary flex h-16 w-full flex-row items-center gap-2 rounded-lg border bg-white pr-12 pl-4 focus-visible:outline-none"
          placeholder="Select your preferred pharmacy"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setError(null)
            setSearchText(event.target.value)
          }}
        />
      </span>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-1">
        {filteredPharmacies?.map((pharmacy) => (
          <PharmacyCard key={pharmacy.id} pharmacy={pharmacy} onClick={handlePharmacySelected} isSelected={selectedPharmacy?.id === pharmacy.id} />
        ))}
      </div>

      {error && (
        <span className="animate-fade-in text-error text-sm" data-testid="error-span">
          {t(error)}
        </span>
      )}

      <Button onClick={handleContinue} loading={loading}>
        {t('button.continue')}
      </Button>
    </BackgroundPattern>
  )
}
